import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  TrendingUp, TrendingDown, Euro, Users, CalendarDays, Wallet,
  MapPin, Clock, Video, Pencil, Star, Flag, CheckCircle2, Calendar, FileSpreadsheet, Loader2, GripVertical,
} from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import {
  MergedToolbar,
  PipelineWidget, QualiopiWidget, TopClientsWidget,
  TeamWidget, StreakWidget, LayoutCustomizer, useDashboardData, PresetPicker,
} from '../components/EnhancedDashboardWidgets';
import TasksWidget from '../components/TasksWidget';
import {
  OverdueInvoicesWidget, CaEvolutionWidget, ClientsFollowupWidget,
  RecentSignaturesWidget, useKpiCaSigne,
} from '../components/FinancialWidgets';
import {
  ConversionRateWidget, FillRateWidget, SessionsToSignWidget,
  ConventionsToSignWidget, UnreadEmailsWidget, TodaySessionsWidget,
  NewLearnersMonthWidget,
} from '../components/OperationalWidgets';
import { FileSignature } from 'lucide-react';

const ResponsiveGridLayout = WidthProvider(Responsive);

const iconByType = { pen: Pencil, euro: Euro, star: Star, user: Users, check: CheckCircle2, flag: Flag };

const PERIODS = [
  { key: '7d', label: '7 jours' },
  { key: '30d', label: '30 jours' },
  { key: '90d', label: '90 jours' },
  { key: '1y', label: '1 an' },
];

// =============================================================================
// KPI card — usable inside a draggable grid item
// =============================================================================
function KpiCard({ icon: Icon, label, value, trend, suffix = '', tint, to, hint, editMode }) {
  const hasTrend = trend !== null && trend !== undefined;
  const up = hasTrend && trend >= 0;
  // In edit mode, disable the Link (we want to drag, not navigate)
  const Wrapper = to && !editMode ? Link : 'div';
  const wrapperProps = to && !editMode ? { to, title: hint || `Voir ${label}` } : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`soft-card p-3 h-full w-full flex flex-col ${to && !editMode ? 'hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4F46E5]' : ''}`}
    >
      {/* Top : icon + label aligned left, trend badge right */}
      <div className="flex items-center justify-between gap-2 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: tint.bg }}>
            <Icon className="w-[15px] h-[15px]" style={{ color: tint.fg }} strokeWidth={2.2} />
          </div>
          <div className="text-[11px] text-[#8E8FA5] font-medium truncate">{label}</div>
        </div>
        {hasTrend ? (
          <span className={`inline-flex items-center gap-0.5 text-[10.5px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${up ? 'bg-[#E6FBF7] text-[#0F766E]' : 'bg-[#FEF2F2] text-[#B91C1C]'}`}>
            {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            {up ? '+' : ''}{trend}%
          </span>
        ) : (
          <span className="inline-flex items-center gap-0.5 text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 flex-shrink-0">—</span>
        )}
      </div>
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center">
        <div className="text-[20px] font-bold text-[#1A1B2E] leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}{suffix}
        </div>
        <div className="text-[10.5px] text-[#8E8FA5] mt-1">vs période précédente</div>
      </div>
    </Wrapper>
  );
}

function DistributionDonut({ data }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let cumul = 0;
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className="soft-card p-3 h-full w-full flex flex-col min-h-0">
      <div className="drag-handle">
        <h3 className="text-[13px] font-semibold text-[#1A1B2E] flex-shrink-0" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Modes de formation</h3>
        <p className="text-[10.5px] text-[#8E8FA5] mb-2 flex-shrink-0">Répartition des sessions</p>
      </div>
      <div className="flex items-center gap-3 flex-1 min-h-0">
        <svg width="110" height="110" viewBox="0 0 130 130" className="-rotate-90 shrink-0">
          <circle cx="65" cy="65" r={radius} fill="none" stroke="#F3F4F8" strokeWidth="14" />
          {data.map((d, i) => {
            const pct = d.value / total;
            const dash = pct * circumference;
            const el = (
              <circle key={i} cx="65" cy="65" r={radius} fill="none" stroke={d.color} strokeWidth="14"
                strokeDasharray={`${dash} ${circumference - dash}`} strokeDashoffset={-cumul * circumference}
                className="transition-all duration-500" />
            );
            cumul += pct;
            return el;
          })}
        </svg>
        <div className="flex-1 space-y-1 min-w-0 overflow-y-auto -mr-1 pr-1">
          {data.map((d, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0"><span className="w-2 h-2 rounded-sm shrink-0" style={{ background: d.color }} /><span className="text-[11.5px] text-[#4A4B63] truncate">{d.label}</span></div>
              <span className="text-[11.5px] font-semibold text-[#1A1B2E]">{d.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UpcomingWidget({ items }) {
  return (
    <div className="soft-card p-3 h-full w-full flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-2 flex-shrink-0 drag-handle">
        <div>
          <h3 className="text-[13px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Prochaines sessions</h3>
          <p className="text-[10.5px] text-[#8E8FA5]">Les {items.length} prochaines à animer</p>
        </div>
        <Link to="/app/sessions" className="text-[11px] text-[#4F46E5] font-semibold hover:underline">Voir tout</Link>
      </div>
      <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto -mr-1 pr-1" data-testid="upcoming-scroll">
        {items.length === 0 && (
          <div className="text-[11.5px] text-[#8E8FA5] py-4 text-center">Aucune session à venir.</div>
        )}
        {items.map((s) => {
          const dt = s.startDate ? new Date(s.startDate) : null;
          const valid = dt && !Number.isNaN(dt.getTime());
          const dayNum = valid ? dt.getDate() : '–';
          const monthLabel = valid ? dt.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '') : '–';
          return (
            <div key={s.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[#FAFAFE] border border-transparent hover:border-[#EFF0F6]">
              <div className="w-10 h-10 rounded-lg brand-gradient-soft flex flex-col items-center justify-center shrink-0">
                <span className="text-[9px] font-semibold text-[#4F46E5] uppercase">{monthLabel}</span>
                <span className="text-[13px] font-bold text-[#3730A3] leading-none">{dayNum}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold text-[#1A1B2E] truncate">{s.title}</div>
                <div className="flex items-center gap-2 text-[10.5px] text-[#6B6C85] mt-0.5 flex-wrap">
                  <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{s.time || '09:00 - 17:00'}</span>
                  <span className="flex items-center gap-0.5">{s.mode === 'Distanciel' ? <Video className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}{s.location}</span>
                  <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{s.learners}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityWidget({ items }) {
  return (
    <div className="soft-card p-3 h-full w-full flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-2 flex-shrink-0 drag-handle">
        <h3 className="text-[13px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Activité récente</h3>
      </div>
      <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto -mr-1 pr-1" data-testid="activity-scroll">
        {items.length === 0 && (
          <div className="text-[11.5px] text-[#8E8FA5] py-4 text-center">Aucune activité récente</div>
        )}
        {items.map((a) => {
          const Icon = iconByType[a.icon] || CheckCircle2;
          const Wrapper = a.link ? Link : 'div';
          const wrapperProps = a.link ? { to: a.link, title: 'Voir le détail' } : {};
          return (
            <Wrapper
              key={a.id}
              {...wrapperProps}
              className={`flex gap-2 -mx-1 px-1 py-1 rounded-md ${a.link ? 'hover:bg-[#EEF2FF] cursor-pointer transition-colors' : ''}`}
              data-testid={`activity-item-${a.id}`}
            >
              <div className="w-6 h-6 rounded-md bg-[#EEF2FF] flex items-center justify-center shrink-0"><Icon className="w-3 h-3 text-[#4F46E5]" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] text-[#1A1B2E] leading-snug">{a.text}</div>
                <div className="text-[10.5px] text-[#8E8FA5] flex items-center gap-1">
                  {a.time}
                  {a.link && <span className="text-[#4F46E5] font-semibold">· Voir →</span>}
                </div>
              </div>
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN Dashboard with react-grid-layout (drag + resize + persistence)
// =============================================================================
export default function Dashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [distribution, setDistribution] = useState([]);
  const [activity, setActivity] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [period, setPeriod] = useState('30d');
  const [editMode, setEditMode] = useState(false);

  // From the enriched widgets hook (overview + layout + grid)
  const { data: overview, layout, grid, toggleWidget, saveGrid, resetLayout, applyPreset, savedAt, saveError } = useDashboardData();
  const [showSavedFlash, setShowSavedFlash] = useState(false);

  useEffect(() => {
    if (!savedAt) return;
    setShowSavedFlash(true);
    const t = setTimeout(() => setShowSavedFlash(false), 1800);
    return () => clearTimeout(t);
  }, [savedAt]);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/kpis'),
      api.get('/dashboard/sessions-distribution'),
      api.get('/dashboard/recent-activity'),
      api.get('/dashboard/upcoming-sessions'),
    ]).then(([a, c, d, e]) => {
      setKpis(a.data);
      setDistribution(c.data);
      setActivity(d.data);
      setUpcoming(e.data);
    }).catch(() => {});
    api.get('/users/me/preferences').then(({ data }) => {
      if (data?.dashboard_period) setPeriod(data.dashboard_period);
    }).catch(() => {});
  }, []);

  const onPeriodChange = (p) => {
    setPeriod(p);
    api.put('/users/me/preferences', { dashboard_period: p }).catch(() => {});
  };

  const [nsfPreview, setNsfPreview] = useState(null);
  const [nsfOpen, setNsfOpen] = useState(false);
  const [nsfLoading, setNsfLoading] = useState(false);

  const openNsfReport = async () => {
    setNsfOpen(true);
    if (nsfPreview) return;
    setNsfLoading(true);
    try {
      const { data } = await api.get('/analytics/nsf-report');
      setNsfPreview(data);
    } catch {
      toast({ title: 'Erreur', description: 'Chargement du bilan impossible.' });
    } finally {
      setNsfLoading(false);
    }
  };

  const downloadNsfCsv = async () => {
    try {
      const res = await api.get('/analytics/nsf-report.csv', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const y = new Date().getFullYear();
      a.href = href; a.download = `bilan-pedagogique-nsf-${y}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(href);
      toast({ title: 'CSV téléchargé', description: 'Prêt pour Excel, LibreOffice ou votre OPCO.' });
    } catch {
      toast({ title: 'Erreur', description: 'Export impossible.' });
    }
  };

  // Hook : CA signé KPI (suit le filtre période)
  const kpiCaSigne = useKpiCaSigne(period);

  const firstName = (user?.name || '').split(' ')[0] || 'Camille';
  const kpiData = kpis || {
    ca: { value: 0, trend: 0, label: "Chiffre d'affaires" },
    sessions: { value: 0, trend: 0, label: 'Sessions actives' },
    apprenants: { value: 0, trend: 0, label: 'Apprenants' },
    tresorerie: { value: 0, trend: 0, label: 'Trésorerie du mois' },
  };

  // Build grid layouts for ResponsiveGridLayout. Use the user's grid (if set), or fall back to defaults.
  const layouts = useMemo(() => {
    if (grid && grid.lg) return grid;
    return null;
  }, [grid]);

  const show = (k) => layout[k] !== false;

  // Define render map: key -> JSX
  const widgetRender = {
    kpi_ca: <KpiCard icon={Euro} label={kpiData.ca.label} value={kpiData.ca.value} trend={kpiData.ca.trend} suffix=" €" tint={{ bg: '#EEF2FF', fg: '#4F46E5' }} to="/app/factures?status=Payée" hint="Voir les factures payées" editMode={editMode} />,
    kpi_sessions: <KpiCard icon={CalendarDays} label={kpiData.sessions.label} value={kpiData.sessions.value} trend={kpiData.sessions.trend} tint={{ bg: '#E6FBF7', fg: '#0F766E' }} to="/app/sessions?status=En+cours" hint="Voir les sessions en cours" editMode={editMode} />,
    kpi_apprenants: <KpiCard icon={Users} label={kpiData.apprenants.label} value={kpiData.apprenants.value} trend={kpiData.apprenants.trend} tint={{ bg: '#FEF3E7', fg: '#B45309' }} to="/app/learners" hint="Voir tous les apprenants" editMode={editMode} />,
    kpi_tresorerie: <KpiCard icon={Wallet} label={kpiData.tresorerie?.label || 'Trésorerie du mois'} value={kpiData.tresorerie?.value || 0} trend={kpiData.tresorerie?.trend} suffix=" €" tint={{ bg: '#FCE7F3', fg: '#BE185D' }} to="/app/factures" hint="Voir les factures et encaissements" editMode={editMode} />,
    kpi_ca_signe: <KpiCard icon={FileSignature} label={kpiCaSigne?.label || 'CA signé (devis)'} value={kpiCaSigne?.value || 0} trend={kpiCaSigne?.trend} suffix=" €" tint={{ bg: '#ECFDF5', fg: '#059669' }} to="/app/devis?status=Signé" hint={`${kpiCaSigne?.count || 0} devis signés sur la période`} editMode={editMode} />,
    toolbar: <MergedToolbar todos={overview?.todos} />,
    pipeline: overview && <PipelineWidget pipeline={overview.pipeline} />,
    tasks: <TasksWidget />,
    overdue_invoices: <OverdueInvoicesWidget />,
    clients_followup: <ClientsFollowupWidget />,
    ca_evolution: <CaEvolutionWidget period={period} />,
    recent_signatures: <RecentSignaturesWidget />,
    // ─── New operational widgets ───
    conversion_rate: <ConversionRateWidget period={period} />,
    fill_rate: <FillRateWidget />,
    sessions_to_sign: <SessionsToSignWidget />,
    conventions_to_sign: <ConventionsToSignWidget />,
    unread_emails: <UnreadEmailsWidget />,
    today_sessions: <TodaySessionsWidget />,
    new_learners_month: <NewLearnersMonthWidget />,
    qualiopi: overview && <QualiopiWidget qualiopi={overview.qualiopi} />,
    top_clients: overview && <TopClientsWidget topClients={overview.top_clients} churnRisk={overview.churn_risk} />,
    team: overview && <TeamWidget team={overview.team} />,
    streak: overview && <StreakWidget streak={overview.streak} />,
    distribution: <DistributionDonut data={distribution} />,
    upcoming: <UpcomingWidget items={upcoming} />,
    activity: <ActivityWidget items={activity} />,
  };

  // Compute visible widgets only
  const visibleKeys = Object.keys(widgetRender).filter((k) => show(k));

  return (
    <div className="flex flex-col gap-3" data-testid="dashboard-scroll-wrap">
      {/* Header compact : Bonjour + période + Personnaliser */}
      <div className="rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap flex-none" style={{ background: 'linear-gradient(120deg, #EEF2FF 0%, #E6FBF7 100%)' }}>
        <div>
          <div className="inline-flex items-center gap-1.5 bg-white/80 backdrop-blur px-2 py-0.5 rounded-full text-[10.5px] font-semibold text-[#3730A3] mb-1">
            <span className="pulse-dot" /> Avril 2025 · Semaine 17
          </div>
          <h2 className="text-[16px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Bonjour {firstName}, votre activité progresse.
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="dashboard-nsf-export-btn"
            onClick={openNsfReport}
            className="h-8 px-3 rounded-lg bg-white border border-[#C7D2FE] text-[#4F46E5] text-[11.5px] font-semibold flex items-center gap-1 hover:bg-[#EEF2FF] transition-colors"
            title="Bilan pédagogique par spécialité NSF (Qualiopi / OPCO)"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Bilan NSF
          </button>
          <div className="flex items-center h-8 bg-white rounded-lg border border-[#EFF0F6] p-0.5">
            <Calendar className="w-3.5 h-3.5 text-[#8E8FA5] mx-1.5" />
            {PERIODS.map((p) => (
              <button
                key={p.key}
                data-testid={`period-${p.key}`}
                onClick={() => onPeriodChange(p.key)}
                className={`h-7 px-2.5 rounded-md text-[11px] font-semibold transition-colors ${period === p.key ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85]'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <LayoutCustomizer
            layout={layout}
            onToggle={toggleWidget}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            onReset={resetLayout}
          />
          <PresetPicker onApply={applyPreset} currentLayoutSnapshot={{ layout, grid }} />
        </div>
      </div>

      {/* Edit mode banner */}
      {editMode && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 flex items-center gap-2 text-indigo-900 flex-none" data-testid="dashboard-edit-banner">
          <GripVertical className="w-4 h-4 flex-shrink-0" />
          <div className="text-[12px] flex-1">
            <span className="font-semibold">Mode édition actif</span> — déplacez les tuiles en saisissant leur en-tête, redimensionnez-les en tirant les coins (bas droit). Cliquez « Terminer » pour verrouiller.
          </div>
          {showSavedFlash && !saveError && (
            <span data-testid="dashboard-saved-flash" className="inline-flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold animate-pulse">
              <CheckCircle2 className="w-3 h-3" /> Enregistré
            </span>
          )}
          {saveError && (
            <span data-testid="dashboard-save-error" className="inline-flex items-center gap-1 rounded-md bg-red-100 text-red-800 px-2 py-0.5 text-[11px] font-semibold">
              ⚠ Sauvegarde échouée
            </span>
          )}
        </div>
      )}

      {/* NSF preview modal (inline, no extra imports) */}
      {nsfOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setNsfOpen(false)} data-testid="nsf-modal">
          <div className="bg-white rounded-2xl max-w-[960px] w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[#EFF0F6] flex items-center justify-between">
              <div>
                <h3 className="text-[17px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Bilan pédagogique par spécialité NSF</h3>
                <p className="text-[12.5px] text-[#8E8FA5]">Aperçu {nsfPreview?.year} · Qualiopi / France Compétences / OPCO</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={downloadNsfCsv} className="h-9 px-3.5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 shadow-sm">
                  <FileSpreadsheet className="w-4 h-4" /> Télécharger CSV
                </button>
                <button onClick={() => setNsfOpen(false)} className="h-9 px-3 rounded-lg border border-[#EFF0F6] text-[#4A4B63] text-[12.5px] font-semibold">Fermer</button>
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {nsfLoading && (
                <div className="text-center py-10 text-[#8E8FA5] flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Agrégation en cours…</div>
              )}
              {!nsfLoading && nsfPreview && nsfPreview.rows?.length === 0 && (
                <div className="text-center py-10 text-[#8E8FA5]">Aucune donnée à agréger. Ajoutez un code NSF sur vos programmes.</div>
              )}
              {!nsfLoading && nsfPreview && nsfPreview.rows?.length > 0 && (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5] border-b border-[#EFF0F6] bg-[#FAFAFE] sticky top-0">
                      <th className="py-3 px-4">Code</th>
                      <th className="py-3 px-4">Libellé NSF</th>
                      <th className="py-3 px-4 text-right">Progr.</th>
                      <th className="py-3 px-4 text-right">Sessions</th>
                      <th className="py-3 px-4 text-right">Heures·stag.</th>
                      <th className="py-3 px-4 text-right">Apprenants</th>
                      <th className="py-3 px-4 text-right">Note /5</th>
                      <th className="py-3 px-4 text-right">Évaluations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nsfPreview.rows.map((r) => (
                      <tr key={r.nsf_code} className="border-b border-[#F3F4F8] last:border-0 hover:bg-[#FAFAFE]">
                        <td className="py-2.5 px-4 font-mono text-[12.5px] font-semibold text-[#4F46E5]">{r.nsf_code}</td>
                        <td className="py-2.5 px-4 text-[12.5px] text-[#1A1B2E]">{r.nsf_label}</td>
                        <td className="py-2.5 px-4 text-[13px] text-right font-semibold">{r.program_count}</td>
                        <td className="py-2.5 px-4 text-[13px] text-right font-semibold">{r.session_count}</td>
                        <td className="py-2.5 px-4 text-[13px] text-right">{r.hours_delivered.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}</td>
                        <td className="py-2.5 px-4 text-[13px] text-right">{r.learner_count}</td>
                        <td className="py-2.5 px-4 text-[13px] text-right font-semibold text-[#F59E0B]">{r.rating_avg ?? '—'}</td>
                        <td className="py-2.5 px-4 text-[12px] text-right text-[#8E8FA5]">{r.evaluation_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-6 py-3 border-t border-[#EFF0F6] bg-[#FAFAFE] text-[11.5px] text-[#6B6C85]">
              ✨ Document prêt à transmettre à votre OPCO ou à joindre à votre dossier d'audit Qualiopi.
            </div>
          </div>
        </div>
      )}

      {/* Responsive Grid Layout — drag/resize when editMode === true */}
      {layouts && (
        <ResponsiveGridLayout
          className={`layout ${editMode ? 'rgl-edit-mode' : 'rgl-locked'}`}
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          cols={{ lg: 12, md: 12, sm: 6, xs: 2 }}
          rowHeight={50}
          margin={[8, 8]}
          containerPadding={[0, 0]}
          isDraggable={editMode}
          isResizable={editMode}
          draggableHandle=".drag-handle"
          compactType="vertical"
          // Save ONLY when the user finishes dragging or resizing (no spam during the gesture).
          onDragStop={(_lay, _o, _n, _p, _e, _el) => {
            if (!editMode) return;
            // _lay is the current breakpoint's layout — we need allLayouts. Read from RGL via its state through layouts prop.
            // Trick : the saveGrid hook accepts a partial { lg: [...] }, backend merges.
            saveGrid({ lg: _lay });
          }}
          onResizeStop={(_lay) => {
            if (!editMode) return;
            saveGrid({ lg: _lay });
          }}
        >
          {visibleKeys.map((k) => (
            <div key={k} data-testid={`grid-tile-${k}`}>
              <div className={`rgl-inner ${editMode ? 'rgl-tile-edit' : ''}`}>
                {widgetRender[k]}
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </div>
  );
}
